import { useState, useEffect } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import { Button } from './Button'
import { useTranslation } from "react-i18next";

const COOKIE_VERSION = '1.0'
const CONSENT_COOKIE_NAME = 'sushiking_cookie_consent'
const FULL_CONSENT_COOKIE_NAME = 'sushiking_full_consent'

const setCookie = (name: string, value: string, options: any = {}) => {
    try {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

        const defaults: any = {
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: isMobile ? 'lax' : 'strict',
            maxAge: 31536000 // 1 year
        }

        if (isIOS && defaults.sameSite === 'strict') {
            defaults.sameSite = 'lax'
        }

        const cookieOptions = { ...defaults, ...options }
        let cookieString = `${name}=${encodeURIComponent(value)}`

        Object.entries(cookieOptions).forEach(([key, val]) => {
            if (val !== undefined && val !== null) {
                if (typeof val === 'boolean') {
                    if (val) cookieString += `; ${key}`
                } else {
                    cookieString += `; ${key}=${val}`
                }
            }
        })

        document.cookie = cookieString
        localStorage.setItem(`cookie_${name}`, value)
    } catch (error) {
        console.error('Error setting cookie:', error)
        try {
            localStorage.setItem(`cookie_${name}`, value)
        } catch (storageError) {
            console.error('Failed to set cookie and localStorage fallback:', storageError)
        }
    }
}

const getCookie = (name: string) => {
    try {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) {
            return decodeURIComponent(parts.pop()?.split(';').shift() || '')
        }
        
        return localStorage.getItem(`cookie_${name}`)
    } catch (error) {
        return localStorage.getItem(`cookie_${name}`)
    }
}

export const CookieManager = {
    getConsent: () => {
        const consent = getCookie(CONSENT_COOKIE_NAME)
        if (!consent) return null
        const parsed = JSON.parse(consent)
        // Check for version match
        if (parsed.version !== COOKIE_VERSION) return null
        return parsed
    },

    setConsent: (preferences: any, legalAcceptance: any = {}) => {
        const consentData = {
            timestamp: new Date().toISOString(),
            version: COOKIE_VERSION,
            preferences,
            userAgent: navigator.userAgent,
            language: navigator.language
        }

        const fullConsentData = {
            ...consentData,
            legal: {
                terms: legalAcceptance.terms || false,
                privacy: legalAcceptance.privacy || false,
                cookies: true,
                marketing: legalAcceptance.marketing || false,
                timestamp: new Date().toISOString()
            }
        }

        setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consentData))
        setCookie(FULL_CONSENT_COOKIE_NAME, JSON.stringify(fullConsentData))

        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
            detail: preferences
        }))
    },

    hasValidConsent: () => {
        const consent = CookieManager.getConsent()
        return consent !== null
    }
}

export const CookieConsentModal = () => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false)
    const [showDetails, setShowDetails] = useState(false)

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (!CookieManager.hasValidConsent()) {
                setShowModal(true)
            }
        }, 500)
        return () => window.clearTimeout(timer)
    }, [])

    const [legal, setLegal] = useState({
        terms: false,
        privacy: false,
        dataProcessing: false
    })

    const [cookies, setCookies] = useState({
        functional: false,
        analytics: false,
        marketing: false
    })

    const handleLegalToggle = (type: keyof typeof legal) => {
        setLegal(prev => ({ ...prev, [type]: !prev[type] }))
    }

    const handleCookieToggle = (type: keyof typeof cookies) => {
        setCookies(prev => ({ ...prev, [type]: !prev[type] }))
    }

    const handleAcceptAll = () => {
        const allCookies = {
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
        }
        
        const allLegal = {
            terms: true,
            privacy: true,
            marketing: true,
            dataProcessing: true,
        }
        
        CookieManager.setConsent(allCookies, allLegal)
        setShowModal(false)
        window.location.reload() // Reload to apply scripts
    }

    const handleAcceptSelected = () => {
        if (!legal.terms || !legal.privacy || !legal.dataProcessing) {
            alert('Proszę zaakceptować wszystkie zgody prawne, aby móc kontynuować (Regulamin, Polityka Prywatności, RODO).')
            return
        }

        const selectedCookies = {
            necessary: true,
            functional: cookies.functional,
            analytics: cookies.analytics,
            marketing: cookies.marketing,
        }
        
        const selectedLegal = {
            terms: true,
            privacy: true,
            marketing: cookies.marketing,
            dataProcessing: true,
        }
        
        CookieManager.setConsent(selectedCookies, selectedLegal)
        setShowModal(false)
        window.location.reload()
    }

    const handleAcceptOnlyNecessary = () => {
        const necessaryCookies = {
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        }
        
        const minimalLegal = {
            terms: true,
            privacy: true,
            marketing: false,
            dataProcessing: true,
        }
        
        CookieManager.setConsent(necessaryCookies, minimalLegal)
        setShowModal(false)
        window.location.reload()
    }

    if (!showModal) return null

    return (
        <>
            <m.div 
                className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
            />
            
            <m.div 
                className="fixed inset-0 z-[100000] flex flex-col items-center justify-center p-4 lg:p-8"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
                <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-full">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between shrink-0 gap-4">
                        <div>
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                                {t('wazneWymaganiaPlatformySushiKing')}</h2>
                            <p className="text-gray-500 text-sm mt-1 max-w-xl">
                                {t('szanujemyTwojPrywatnoscZanimZaczniesz')}</p>
                        </div>
                    </div>

                    <div className="p-6 overflow-y-auto grow space-y-6">
                        <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                            <button 
                                className="w-full bg-gray-50 p-4 flex justify-between items-center text-sm font-bold text-gray-700 hover:bg-gray-100 transition-colors"
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                <span>{t('recznyWyborZgodMozliwoscWylczenia')}</span>
                                <span className={`text-lg transition-transform ${showDetails ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            
                            <AnimatePresence>
                                {showDetails && (
                                    <m.div 
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden bg-white"
                                    >
                                        <div className="p-6 space-y-8">
                                            <div>
                                                <h4 className="font-black text-gray-900 mb-3 text-lg border-b pb-2">{t('1WymaganeZgodyPrawne')}{' '}<span className="text-red-500 text-sm font-normal">{t('opcjeObowizkowe')}</span></h4>
                                                <div className="space-y-3">
                                                    <label className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked={legal.terms} onChange={() => handleLegalToggle('terms')} />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('zgadzamSieNa')}<a href="/regulamin" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{t('regulaminSerwisu')}</a></p>
                                                        </div>
                                                    </label>

                                                    <label className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked={legal.privacy} onChange={() => handleLegalToggle('privacy')} />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('zgadzamSieNa')}<a href="/polityka-prywatnosci" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{t('politykePrywatnosci')}</a> {t('orazCookies')}</p>
                                                        </div>
                                                    </label>

                                                    <label className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked={legal.dataProcessing} onChange={() => handleLegalToggle('dataProcessing')} />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('zgodaNaRodoPrzetwarzanieDanych')}</p>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-black text-gray-900 mb-3 text-lg border-b pb-2">{t('2PreferencjePlikowCookie')}</h4>
                                                <div className="space-y-3">
                                                    <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg opacity-80 cursor-not-allowed">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked disabled />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('niezbedneZawszeAktywne')}</p>
                                                            <p className="text-xs text-gray-500 mt-1">{t('wymaganeMinDoBezpiecznegoLogowania')}</p>
                                                        </div>
                                                    </div>

                                                    <label className="flex items-start gap-4 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked={cookies.functional} onChange={() => handleCookieToggle('functional')} />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('funkcjonalneIAnalityczne')}</p>
                                                            <p className="text-xs text-gray-500 mt-1">{t('pomagajNamUlepszacWitryneNa')}</p>
                                                        </div>
                                                    </label>

                                                    <label className="flex items-start gap-4 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                                        <input type="checkbox" className="mt-1 w-5 h-5 text-primary focus:ring-primary rounded border border-gray-300" checked={cookies.marketing} onChange={() => handleCookieToggle('marketing')} />
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{t('marketingowe')}</p>
                                                            <p className="text-xs text-gray-500 mt-1">{t('umozliwiajUdostepnianieTrafnychReklamI')}</p>
                                                        </div>
                                                    </label>
                                                </div>
                                                <div className="mt-6 flex justify-end">
                                                    <Button variant="outline" onClick={handleAcceptSelected}>{t('zapiszMojNiestandardowyWyborZgod')}</Button>
                                                </div>
                                            </div>
                                        </div>
                                    </m.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="p-6 border-t border-gray-100 bg-gray-50 shrink-0 flex flex-col gap-4 justify-between items-center relative">
                        <button 
                            onClick={handleAcceptAll}
                            className="w-full relative group overflow-hidden bg-primary text-white font-black text-xl py-6 rounded-xl shadow-[0_0_40px_rgba(239,35,60,0.5)] hover:shadow-[0_0_60px_rgba(239,35,60,0.8)] transition-all transform hover:scale-[1.03] active:scale-[0.98]"
                        >
                            <span className="relative z-10 flex items-center justify-center gap-3">
                                {t('zaakceptujWszystkoIPrzejdzDo')}</span>
                            <div className="absolute inset-0 h-full w-full object-cover bg-gradient-to-r from-red-500 via-primary to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                        </button>

                        <button 
                            onClick={handleAcceptOnlyNecessary}
                            className="text-[11px] text-gray-400 hover:text-gray-600 underline decoration-gray-300 underline-offset-4 opacity-50 hover:opacity-100 transition-opacity"
                        >
                            {t('chceWylczycCookiesOpcjonalneA')}</button>
                    </div>
                </div>
            </m.div>
        </>
    )
}
